import { useContext, useState } from 'react';
import { useQuery } from 'react-apollo';
import { StaticContext } from '@sm/webassets';
import { getClientEnvironmentDetails } from '@sm/utils';

import { getPricingPageExperience } from './PricingExperience.graphql';

/**
 * If the pricing page experience test comes back with price test cookie
 * values to save, do that. This is mostly for logged out users, but can
 * also apply to experiment preview cookies.
 *
 * @param {*} priceTest The price test object returned from experimentsvc
 */
const savePriceTestExperimentCookies = cookies => {
  // Set each cookie the same way it's done in <Treatment>
  const { host } = window.location;
  const domain = host.replace(/^[^.]+\./g, '');
  cookies.forEach(cookieObj => {
    document.cookie = `${cookieObj.name}=${cookieObj.value}; max-age=${cookieObj.age}; path=/; domain=${domain}`;
  });
};

/**
 * Hook to fetch pricing experience data from GraphAPI
 *
 * @param {array} encryptParams Array of tracking parameters to be encryted with the package CTA
 * @param {string | undefined} experienceName The experience name to fetch from pricingsvc. If passed,
 *                                            will override the check for price test
 */
export default function usePricingExperience({ encryptParams, experienceName } = {}) {
  const {
    environment: { countryCode, languageCode },
  } = useContext(StaticContext);
  const [priceTestCookies, setPriceTestCookies] = useState(null);
  const { loading, error, data } = useQuery(getPricingPageExperience, {
    variables: {
      countryCode,
      languageCode,
      experienceName,
      encryptParams,
    },
    onCompleted: () => {
      // If price test cookies came back from the pricing experience call,
      // stash them in state so that when this code gets executed on the browser
      // we can then save the cookie appropriately
      setPriceTestCookies(data && data.pricingPageExperience.priceTestCookies);
    },
  });

  // This is a bit of a hack to ensure that any price test cookies that came back
  // during SSR get saved to the clients browser upon rehydration
  const { isBrowser } = getClientEnvironmentDetails();
  if (Array.isArray(priceTestCookies) && isBrowser) {
    savePriceTestExperimentCookies(priceTestCookies);
  }

  return { loading, data, error };
}
